import { useUnmount } from 'react-use';
import { subDays } from 'date-fns';
import { devtools } from 'zustand/middleware';

import { formatDateTime } from '@/shared/helpers/format.helpers';
import { createStoreWithReset } from '@/shared/lib/zustand';
import type { OrderDataGridRowData } from '@/shared/types/orders.types';

import type { OrderDataGridSelectedRowsData } from './types';

const now = Date.now();
const aWeekAgo = formatDateTime(subDays(now, 7).getTime(), 'yyyy-MM-dd') as string;
const today = formatDateTime(now, 'yyyy-MM-dd') as string;
const DEFAULT_OFFERS_PERIOD = [aWeekAgo, today];

type OrdersStore = {
  isFiltersPanelVisible: boolean;
  isReconciliationUploadModalVisible: boolean;
  isReconciliationLogModalVisible: boolean;
  reconciliationLogOrderGuid: string;
  pendingReconciliationUploadOrderGuid: string | null;

  isProcessingMainDispatchAction: boolean;
  isProcessingAnyInlineDispatchAction: boolean;

  period: string[];
  searchQuery: string;

  selectedRowsData: OrderDataGridSelectedRowsData;
  contextRowData: OrderDataGridRowData | null;

  actions: {
    openFiltersPanel: () => void;
    closeFiltersPanel: () => void;

    openReconciliationUploadModal: () => void;
    closeReconciliationUploadModal: () => void;

    openReconciliationLogModal: (orderGuid: string) => void;
    closeReconciliationLogModal: () => void;

    setPendingReconciliationUploadOrderGuid: (orderGuid: string) => void;
    clearPendingReconciliationUploadOrderGuid: () => void;

    setIsProcessingMainDispatchAction: (isProcessingMainDispatchAction: boolean) => void;
    setIsProcessingAnyInlineDispatchAction: (isProcessingInlineDispatchAction: boolean) => void;

    setPeriod: (ordersPeriod: string[]) => void;

    setSearchQuery: (query: string) => void;
    resetSearchQuery: () => void;

    setSelectedRowsData: (selectedRowsData: OrderDataGridSelectedRowsData) => void;
    setContextRowData: (contextRowData: OrderDataGridRowData) => void;
  };
};

const [useOrdersStore, resetOrdersStore] = createStoreWithReset<OrdersStore>()(
  devtools(
    set => ({
      isFiltersPanelVisible: false,
      isReconciliationUploadModalVisible: false,
      isReconciliationLogModalVisible: false,
      reconciliationLogOrderGuid: '',
      pendingReconciliationUploadOrderGuid: null,

      isProcessingMainDispatchAction: false,
      isProcessingAnyInlineDispatchAction: false,

      period: DEFAULT_OFFERS_PERIOD,
      searchQuery: '',

      selectedRowsData: {},
      contextRowData: null,
      ordersToDispatch: [],

      actions: {
        openFiltersPanel: () => set({ isFiltersPanelVisible: true }, false, 'openFiltersPanel'),
        closeFiltersPanel: () => set({ isFiltersPanelVisible: false }, false, 'closeFiltersPanel'),

        openReconciliationUploadModal: () =>
          set({ isReconciliationUploadModalVisible: true }, false, 'openReconciliationUploadModal'),
        closeReconciliationUploadModal: () =>
          set({ isReconciliationUploadModalVisible: false }, false, 'closeReconciliationUploadModal'),

        openReconciliationLogModal: (orderGuid: string) =>
          set(
            { isReconciliationLogModalVisible: true, reconciliationLogOrderGuid: orderGuid },
            false,
            'openReconciliationLogModal',
          ),
        closeReconciliationLogModal: () =>
          set(
            { isReconciliationLogModalVisible: false, reconciliationLogOrderGuid: '' },
            false,
            'closeReconciliationLogModal',
          ),

        setPendingReconciliationUploadOrderGuid: orderGuid =>
          set({ pendingReconciliationUploadOrderGuid: orderGuid }, false, 'setPendingReconciliationUploadOrderGuid'),
        clearPendingReconciliationUploadOrderGuid: () =>
          set({ pendingReconciliationUploadOrderGuid: null }, false, 'clearPendingReconciliationUploadOrderGuid'),

        setIsProcessingMainDispatchAction: isProcessingMainDispatchAction =>
          set({ isProcessingMainDispatchAction }, false, 'setIsProcessingMainDispatchAction'),
        setIsProcessingAnyInlineDispatchAction: isProcessingAnyInlineDispatchAction =>
          set({ isProcessingAnyInlineDispatchAction }, false, 'setIsProcessingAnyInlineDispatchAction'),

        setPeriod: period => set({ period }, false, 'setPeriod'),

        setSearchQuery: searchQuery => set({ searchQuery }, false, 'setSearchQuery'),
        resetSearchQuery: () => set({ searchQuery: '' }, false, 'resetSearchQuery'),

        setSelectedRowsData: selectedRowsData => set({ selectedRowsData }, false, 'setSelectedRowsData'),
        setContextRowData: contextRowData => set({ contextRowData }, false, 'setContextRowData'),
      },
    }),
    {
      name: 'useOrdersStore',
    },
  ),
);

// Selectors
const selectFiltersPanelState = (state: OrdersStore) => state.isFiltersPanelVisible;

const selectReconciliationUploadModalState = (state: OrdersStore) => state.isReconciliationUploadModalVisible;
const selectReconciliationLogModalState = (state: OrdersStore) => state.isReconciliationLogModalVisible;
const selectReconciliationLogOrderGuid = (state: OrdersStore) => state.reconciliationLogOrderGuid;
const selectPendingReconciliationUploadOrderGuid = (state: OrdersStore) => state.pendingReconciliationUploadOrderGuid;

const selectContextWindowsState = (state: OrdersStore) => ({
  isReconciliationUploadModalVisible: state.isReconciliationUploadModalVisible,
  isReconciliationLogModalVisible: state.isReconciliationLogModalVisible,
});

const selectMainDispatchActionState = (state: OrdersStore) => state.isProcessingMainDispatchAction;
const selectAnyInlineDispatchActionState = (state: OrdersStore) => state.isProcessingAnyInlineDispatchAction;

const selectPeriod = (state: OrdersStore) => state.period;
const selectSearchQuery = (state: OrdersStore) => state.searchQuery;
const selectSelectedRowsData = (state: OrdersStore) => state.selectedRowsData;
const selectContextRowData = (state: OrdersStore) => state.contextRowData;

const selectActions = (state: OrdersStore) => state.actions;

// Hooks
export const useOrdersStoreFiltersPanelState = () => useOrdersStore(selectFiltersPanelState);
export const useOrdersStoreReconciliationUploadModalState = () => useOrdersStore(selectReconciliationUploadModalState);
export const useOrdersStoreReconciliationLogModalState = () => useOrdersStore(selectReconciliationLogModalState);
export const useOrdersStoreReconciliationLogOrderGuid = () => useOrdersStore(selectReconciliationLogOrderGuid);
export const useOrdersStorePendingReconciliationUploadOrderGuid = () =>
  useOrdersStore(selectPendingReconciliationUploadOrderGuid);
export const useOrdersStoreContextWindowsState = () => useOrdersStore(selectContextWindowsState);

export const useOrdersStoreMainDispatchActionState = () => useOrdersStore(selectMainDispatchActionState);
export const useOrdersStoreAnyInlineDispatchActionState = () => useOrdersStore(selectAnyInlineDispatchActionState);

export const useOrdersStorePeriod = () => useOrdersStore(selectPeriod);
export const useOrdersStoreSearchQuery = () => useOrdersStore(selectSearchQuery);
export const useOrdersStoreSelectedRowsData = () => useOrdersStore(selectSelectedRowsData);
export const useOrdersStoreContextRowData = () => useOrdersStore(selectContextRowData);

export const useOrdersStoreActions = () => useOrdersStore(selectActions);
export const useOrdersStoreResetOnUnmount = () => useUnmount(resetOrdersStore);
